import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import FlatbackImage1 from "@assets/turtle-images/flatback-1.webp";
import FlatbackImage2 from "@assets/turtle-images/flatback-2.webp";
import FlatbackImage3 from "@assets/turtle-images/flatback-3.webp";
import FlatbackImage4 from "@assets/turtle-images/flatback-4.webp";

interface FlatbackSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function FlatbackSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: FlatbackSectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const flatbackCopy = {
    row1: {
      left: {
        title: "True-blue Aussies",
        image: FlatbackImage1,
        imageAlt: "Flatback turtle in shallow coastal water",
        paragraphs: [
          "Unlike all other sea turtles, flatbacks live exclusively near the tropical beaches of northern Australia. They are rarely seen on the Fraser Coast, mostly nesting north around the islands off Rockhampton.",
          "They stay in shallow coastal waters for their entire lives, whereas other turtles spend years in the open ocean. You can often spot them sunbathing on the surface with a seabird perched on their back.",
          "They are fragile with a thinner and softer shell compared to other turtles.",
        ],
      },
      right: {
        title: "Biggest beach babies",
        image: FlatbackImage2,
        imageAlt: "Flatback turtle nesting track on beach",
        paragraphs: [
          "Flatback turtles lay the largest eggs and produce the largest hatchlings of all sea turtles.",
          "You can recognise their unique, symmetrical nesting tracks on the beach: flatbacks move both front flippers forward at the same time, rather than alternating them like other turtles.",
          "If you are lucky enough to spot a turtle nesting, stay still, be silent and give them space. Stay behind them so they can’t see you. Never disturb turtles with a torch or flash photography.",
        ],
      },
    },
    row2: {
      left: {
        title: "Rescuing a flatback floater",
        image: FlatbackImage3,
        imageAlt: "Rescued flatback turtle rehabilitation story",
        imageCaption: "Pikelet almost ready for release. Photo by Matt Barros",
        paragraphs: [
          "A ‘floater’, or a sea turtle with floating syndrome, is unable to dive because of gas caused by eating plastic or marine rubbish, or from injury or infection.",
          "In late 2025, a local boatie found a floater off Burrum Heads. Volunteers, who named the turtle Pikelet, think his injuries were caused by a shark attack.",
          "Pikelet underwent treatment and rehab for 56 days before being released at Burrum Heads with about 50 community members wishing him luck for his onward journey.",
        ],
      },
      right: {
        title: "Fishers, take care!",
        image: FlatbackImage4,
        imageAlt: "Entangled turtle in ghost nets",
        imageCaption: "A loggerhead entangled in ghost nets",
        paragraphs: [
          "Sea turtles are particularly vulnerable to the effects of fishing.",
          "They can get entangled in or amputated by fishing lines, trapped and drowned in crab pots, and swallow fishhooks. Discarded fishing gear, such as ghost nets, can also trap and drown turtles.",
          "If you see a turtle when you are fishing, please pull your lines out immediately and move, because turtles learn to return to bait-heavy areas, increasing their risk.",
          "If you find an injured turtle caught in fishing gear, contact Turtles in Trouble Rescue volunteers as soon as possible. Do not cut lines, try to remove any hooks, or lift an injured turtle.",
        ],
      },
    },
  };

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Flatback turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  const renderSectionBody = (section: {
    title: string;
    paragraphs: string[];
    image?: string;
    imageAlt?: string;
    imageCaption?: string;
  }) => (
    <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
      {section.image ? (
        <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
          <div className="w-full h-[200px] sm:h-[240px] md:h-[265px] overflow-hidden bg-gray-300">
            <img
              src={section.image}
              alt={section.imageAlt || ""}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          {section.imageCaption ? (
            <p className="text-black text-[12px] font-light leading-[1.2em] italic">
              {section.imageCaption}
            </p>
          ) : null}
        </div>
      ) : null}
      <h3
        className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          lineHeight: "1.2em",
        }}
      >
        {section.title}
      </h3>
      <div className="flex flex-col gap-2 sm:gap-3">
        {section.paragraphs.map((paragraph, i) => (
          <p
            key={i}
            className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 300,
              lineHeight: "1.2em",
            }}
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
      style={{ backgroundColor: "#F1F1F1" }}
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
            Flatback turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic">
            {model.scientificName}
          </h3>
        </div>

        <div className="flex flex-col gap-6 sm:gap-[30px]">
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <a
              href="https://sketchfab.com/3d-models/model-53a-flatback-sea-turtle-442372b7f02b4730882d41d959726156"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black text-[12px] font-light leading-[1.2em] italic underline hover:opacity-70"
            >
              sketchfab
            </a>
          </div>
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 sm:gap-[50px]">
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(flatbackCopy.row1.left)}</div>
            <div className="flex-1">{renderSectionBody(flatbackCopy.row1.right)}</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(flatbackCopy.row2.left)}</div>
            <div className="flex-1">{renderSectionBody(flatbackCopy.row2.right)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
